import { compile } from "path-to-regexp";
import React, { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { Link, Route, useHistory, useParams, useRouteMatch } from "react-router-dom";
import { ACTIONS, MODALS, ROUTES, STATUSES } from "../../../constants";
import { Cargo } from "../../../projects/cargo-us";
import * as api from "../../api";
import Button from "../../components/Button";
import InputText from "../../components/InputText";
import PageLoader from "../../components/PageLoader";
import Table from "../../components/Table";
import { useAuth, useModal } from "../../hooks";
import { ModalSwitch } from "../../hooks/useModal";
import { usePrivateRoute } from "../../hooks/usePrivateRoute";
import { useProductCheck } from "../../hooks/useProductCheck";
import { useScrollTop } from "../../hooks/useScrollTop";
import NavBar from "../NavBar";
import { columns, filters } from "./config";
import { getMCSchemaBasedOnRole } from "../../../projects/mc/helpers";

const Contracts = () => {
  const { push } = useHistory();
  const { productRef } = useParams();
  const isPoliciesRoute = useRouteMatch(ROUTES.CONTRACTS) && ROUTES.CONTRACTS;
  const isDraftsRoute = useRouteMatch(ROUTES.DRAFTS) && ROUTES.DRAFTS;
  const isReferralsRoute = useRouteMatch(ROUTES.REFERRALS) && ROUTES.REFERRALS;
  const { isAdmin, role, checkPermissions, user, tenants } = useAuth();

  const [search, setSearch] = useState("");

  useEffect(() => {
    setSearch("");
  }, [isPoliciesRoute, isDraftsRoute, isReferralsRoute]);

  const params = {
    status: (filters[isPoliciesRoute || isDraftsRoute || isReferralsRoute] || []).join(","),
  };
  const contractsQuery = useQuery(["contracts", productRef, params], () => api.getContracts({ productRef, params }), {
    select: (data) => data?.data?.data,
  });
  let contractsQueryData = contractsQuery?.data;

  // search
  if (search?.length !== 0) {
    const searchText = search.trim().toLowerCase();
    const searchTextWithoutHash = searchText.startsWith("#") ? searchText.substring(1) : searchText;

    const searchTenantIds = tenants
      .filter((tenant) => tenant?.name?.toLowerCase()?.indexOf(searchText) !== -1)
      ?.map((tenant) => tenant.id);

    contractsQueryData = contractsQueryData?.filter(
      (contract) =>
        contract?.ref?.toLowerCase()?.indexOf(searchTextWithoutHash) !== -1 ||
        contract?.submission?.general_information?.insured_name?.toLowerCase()?.indexOf(searchText) !== -1 ||
        (isPoliciesRoute &&
          searchTenantIds.length !== 0 &&
          contract?.tenantId &&
          searchTenantIds.includes(contract.tenantId)),
    );
  }

  // tenant name
  contractsQueryData = contractsQueryData?.map((contract) => {
    return { ...contract, tenantName: tenants?.find((tenant) => tenant.id === contract?.tenantId)?.name ?? "" };
  });

  const hasData = contractsQueryData?.length > 0;
  const { checkPermission, isPlacingBroker, isInternalEnv } = useAuth();
  const canDownloadBdx = isPoliciesRoute && (checkPermissions(ACTIONS.DOWNLOAD_BDX) || isPlacingBroker);

  const { showModal } = useModal();

  const handleRowClick = (contractData) => {
    const { status, id } = contractData;
    const canEdit = checkPermission(status)(ACTIONS.UPDATE_SUBMISSION);

    if (canEdit && status === STATUSES.DRAFT) {
      return push(compile(ROUTES.CONTRACT_EDIT)({ productRef, contractId: id }));
    }

    return push(compile(ROUTES.CONTRACT_VIEW)({ productRef, contractId: id }));
  };

  useScrollTop();

  const onSearch = (event) => {
    setSearch(event.target.value);
  };

  return (
    <>
      <ModalSwitch />

      <div className="pt-16 mt-1.5">
        <NavBar />
      </div>

      {contractsQuery.isLoading && <PageLoader />}

      <div className="bg-white shadow rounded m-8">
        {!contractsQuery.isLoading && (hasData || search?.length !== 0) && (
          <Table
            headingText={
              (isPoliciesRoute && "Policies") ||
              (isDraftsRoute && "Drafts and Quotes") ||
              (isReferralsRoute && "Referrals")
            }
            headingDecorator={
              <div className="flex items-center justify-between">
                <InputText
                  labelText=""
                  value={search}
                  className="w-auto px-4"
                  placeholderText="Search"
                  onChange={onSearch}
                />
                {canDownloadBdx && (
                  <Button
                    className="h-10 -my-2"
                    kind="primary"
                    aria-label="Download Bordereau"
                    onClick={() => showModal(MODALS.DOWNLOAD_BDX, { productRef })}
                  >
                    Download Bordereau
                  </Button>
                )}
              </div>
            }
            columns={
              process.env.DEFAULT_PRODUCT_REF === "cargo-us"
                ? Cargo.config.columns
                : columns[isPoliciesRoute || isDraftsRoute || isReferralsRoute]
            }
            rows={contractsQueryData}
            onRowClick={handleRowClick}
          />
        )}

        {!contractsQuery.isLoading && !hasData && search?.length === 0 && (
          <div className="text-center mt-8 py-12">
            <Route path={ROUTES.DRAFTS}>
              <p className="text-2xl font-medium mb-4">Nothing to see here…</p>
              <p className="mb-6">There are no quotes in the system. Click below to get started!</p>
            </Route>

            <Route path={ROUTES.CONTRACTS}>
              <p className="text-2xl font-medium mb-4">It feels a bit empty in here…</p>
              <p className="mb-6">Create a new quote to get started.</p>
            </Route>

            <Route path={ROUTES.REFERRALS}>
              <p className="text-2xl font-medium mb-4">Nothing to see here…</p>
              <p className="mb-6">There are currently no quotes within the system. Click below to get started!</p>
            </Route>

            {checkPermissions(ACTIONS.CREATE_DRAFT_SUBMISSION) && productRef !== "cargo-us" && productRef !== "mc" && (
              <Button
                as={Link}
                to={compile(ROUTES.CONTRACT_NEW)({ productRef })}
                kind="primary"
                aria-label="Create new quote"
              >
                Create new quote
              </Button>
            )}

            {checkPermissions(ACTIONS.CREATE_DRAFT_SUBMISSION) && productRef === "cargo-us" && (
              <Button
                className="h-10 my-4 mr-4"
                onClick={() => showModal(MODALS.SELECT_POLICY_TYPE)}
                kind="primary"
                aria-label="Create new quote"
              >
                Create new quote
              </Button>
            )}

            {checkPermissions(ACTIONS.CREATE_DRAFT_SUBMISSION) && productRef === "mc" && !isAdmin && (
              <Button
                className="h-10 my-4 mr-4"
                onClick={() => {
                  push({
                    pathname: compile(ROUTES.CONTRACT_NEW)({ productRef }),
                    search: `?schemaRef=${getMCSchemaBasedOnRole(productRef, role)}`,
                  });
                }}
                kind="primary"
                aria-label="Create new quote"
              >
                Create new quote
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

const ContractsPage = () => {
  const { isLoading } = usePrivateRoute();

  useProductCheck();

  if (isLoading) {
    return <PageLoader />;
  }

  return <Contracts />;
};

export default ContractsPage;
